import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faVolumeXmark, faVolumeHigh, faEye, faMessage, faUsers, faChevronLeft, faPaperPlane } from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import echo from '../lib/echo';

interface LiveConfig {
    is_live: boolean;
    video_url: string;
    welcome_message: string;
    viewer_count: number;
    scheduled_start: string | null;
    scheduled_end: string | null;
    banner_url: string | null;
}

interface ChatMessage {
    id: string;
    type: 'comment' | 'enter' | 'like' | 'welcome';
    name?: string;
    content: string;
    isAdmin?: boolean;
}

function getSessionId(): string {
    let sid = sessionStorage.getItem('_live_sid');
    if (!sid) {
        sid = crypto.randomUUID();
        sessionStorage.setItem('_live_sid', sid);
    }
    return sid;
}

function getBrowserInfo(): Record<string, string> {
    try {
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
        const tzCountryMap: Record<string, string> = {
            'Asia/Shanghai': 'CN', 'Asia/Chongqing': 'CN', 'Asia/Harbin': 'CN', 'Asia/Urumqi': 'CN',
            'Asia/Hong_Kong': 'HK', 'Asia/Macau': 'MO', 'Asia/Taipei': 'TW',
        };
        const country = tzCountryMap[tz] || '';
        return {
            screen_resolution: `${window.screen.width}x${window.screen.height}`,
            language: navigator.language || '',
            timezone: tz,
            country,
        };
    } catch {
        return {};
    }
}

export default function LiveStream() {
    const navigate = useNavigate();
    const [config, setConfig] = useState<LiveConfig | null>(null);
    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [inputValue, setInputValue] = useState('');
    const [isMuted, setIsMuted] = useState(true);
    const [viewerCount, setViewerCount] = useState(0);
    const videoRef = useRef<HTMLVideoElement>(null);
    const chatRef = useRef<HTMLDivElement>(null);
    const [groupUrl, setGroupUrl] = useState<string>('/group');
    const sentContentRef = useRef<Set<string>>(new Set());

    useEffect(() => {
        api.get('/live/config')
            .then(({ data }) => {
                setConfig(data);
                if (data.viewer_count) {
                    setViewerCount(data.viewer_count);
                }
                if (data.welcome_message) {
                    setMessages([
                        {
                            id: Date.now().toString(),
                            type: 'welcome',
                            content: data.welcome_message,
                            isAdmin: true,
                        },
                    ]);
                }
            })
            .catch((err) => console.error(err));

        api.get('/home-links').then(({ data }) => {
            if (data) {
                if (typeof data.group === 'string' && data.group.length) setGroupUrl(data.group);
            }
        }).catch(() => { });

        const browserInfo = getBrowserInfo();
        const sessionId = getSessionId();

        api.post('/video-streams/track', {
            video_id: 0,
            session_id: sessionId,
            event_type: 'view',
            browser_info: browserInfo,
        }).catch(() => { });

        api.post('/live/enter', { session_id: sessionId, browser_info: browserInfo })
            .then(({ data }) => setViewerCount(data.viewer_count))
            .catch(() => { });

        return () => {
            api.post('/live/leave').catch(() => { });
        };
    }, []);

    useEffect(() => {
        const channel = echo.channel('live-stream');

        channel.listen('.LiveStatusChanged', (e: { is_live: boolean; video_url: string; welcome_message: string }) => {
            setConfig(prev => prev ? { ...prev, is_live: e.is_live, video_url: e.video_url, welcome_message: e.welcome_message } : prev);
        });

        channel.listen('.LiveChatMessage', (e: { id: string; type: string; name: string; content: string; isAdmin: boolean }) => {
            if (e.type === 'comment' && sentContentRef.current.has(e.content)) {
                sentContentRef.current.delete(e.content);
                return;
            }
            setMessages(prev => {
                const next = [...prev, { id: e.id, type: e.type as ChatMessage['type'], name: e.name, content: e.content, isAdmin: e.isAdmin }];
                if (next.length > 50) return next.slice(next.length - 50);
                return next;
            });
        });

        channel.listen('.LiveViewerCount', (e: { count: number }) => {
            setViewerCount(e.count);
        });

        return () => {
            echo.leave('live-stream');
        };
    }, []);

    useEffect(() => {
        if (chatRef.current) {
            chatRef.current.scrollTop = chatRef.current.scrollHeight;
        }
    }, [messages]);

    const handleSend = () => {
        if (!inputValue.trim()) return;
        const content = inputValue.trim();
        setMessages((prev) => [
            ...prev,
            {
                id: Date.now().toString(),
                type: 'comment',
                name: '我',
                content,
            },
        ]);
        setInputValue('');
        sentContentRef.current.add(content);
        api.post('/live/comment', { content }).catch(() => { });
    };

    const toggleMute = () => {
        if (videoRef.current) {
            videoRef.current.muted = !videoRef.current.muted;
            setIsMuted(videoRef.current.muted);
        }
    };

    return (
        <div className="flex flex-col h-screen bg-gray-50 text-[15px]">
            {/* Header */}
            <div className="bg-white flex items-center justify-between px-4 py-3 sticky top-0 z-50">
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => navigate(-1)}
                        className="absolute top-4 left-4 z-10 w-8 h-8 flex items-center justify-center rounded-full bg-white/80 text-black"
                    >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6" /></svg>
                    </button>
                    <div className="flex items-center gap-1.5">
                        <div className="w-6 h-6 bg-red-600 rounded-full flex items-center justify-center text-white text-xs font-bold">
                            中
                        </div>
                        <span className="font-semibold text-gray-900">云家大会</span>
                    </div>
                </div>
                {config?.is_live ? (
                    <div className="flex items-center gap-1.5 px-2 py-0.5 bg-red-50 text-red-600 border border-red-100 rounded-full text-xs font-medium">
                        <div className="w-1.5 h-1.5 bg-red-600 rounded-full animate-pulse" />
                        直播中
                    </div>
                ) : (
                    <div className="flex items-center gap-1.5 px-2 py-0.5 bg-gray-100 text-gray-500 border border-gray-200 rounded-full text-xs font-medium">
                        未开播
                    </div>
                )}
            </div>

            {/* Video Area */}
            <div className="relative w-full aspect-[9/16] max-h-[45vh] bg-black sm:aspect-video sm:max-h-none">
                {config?.is_live && config?.video_url ? (
                    <>
                        <video
                            ref={videoRef}
                            src={config.video_url}
                            className="w-full h-full object-contain"
                            autoPlay
                            loop
                            muted={isMuted}
                            playsInline
                        />

                        <div className="absolute top-3 left-3 flex items-center gap-1.5 px-2.5 py-1 bg-black/40 backdrop-blur-sm rounded-full text-white text-xs">
                            <div className="w-1.5 h-1.5 bg-red-500 rounded-full" />
                            直播中
                        </div>

                        <div className="absolute bottom-3 left-3 flex items-center gap-1.5 px-2.5 py-1 bg-black/40 backdrop-blur-sm rounded-full text-white text-xs">
                            <FontAwesomeIcon icon={faEye} />
                            {viewerCount.toLocaleString()} 人正在观看
                        </div>

                        {isMuted && (
                            <button
                                onClick={toggleMute}
                                className="absolute inset-0 flex flex-col items-center justify-center bg-black/20"
                            >
                                <div className="bg-black/50 backdrop-blur-md px-4 py-3 rounded-2xl flex flex-col items-center gap-2">
                                    <FontAwesomeIcon icon={faVolumeXmark} className="text-white text-2xl" />
                                    <span className="text-white text-sm">点击开启声音</span>
                                </div>
                            </button>
                        )}
                        {!isMuted && (
                            <button
                                onClick={toggleMute}
                                className="absolute top-3 right-3 p-2.5 bg-black/40 backdrop-blur-sm rounded-full text-white flex items-center justify-center"
                            >
                                <FontAwesomeIcon icon={faVolumeHigh} />
                            </button>
                        )}
                    </>
                ) : config?.banner_url ? (
                    <img
                        src={config.banner_url}
                        alt="直播预告"
                        className="w-full h-full object-contain"
                    />
                ) : (
                    <div className="flex flex-col items-center justify-center h-full text-gray-400 gap-3">
                        <div className="w-16 h-16 bg-gray-700 rounded-full flex items-center justify-center">
                            <FontAwesomeIcon icon={faVolumeXmark} className="text-gray-500 text-2xl" />
                        </div>
                        <span className="text-gray-500 text-sm">直播尚未开始，敬请期待</span>
                    </div>
                )}
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 p-3 bg-white">
                <button
                    onClick={() => window.open(groupUrl, '_blank')}
                    className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-blue-500 text-white rounded-xl font-medium shadow-sm shadow-blue-500/20 active:bg-blue-600 transition-colors"
                >
                    <FontAwesomeIcon icon={faUsers} />
                    加入群聊
                </button>
            </div>

            {/* Chat Area */}
            <div
                ref={chatRef}
                className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50 border-t border-gray-100 pb-20"
            >
                {messages.map((msg) => (
                    <div key={msg.id} className="text-[14px] leading-relaxed break-words">
                        {msg.type === 'welcome' && (
                            <div className="inline-block px-3 py-1.5 bg-blue-50 text-blue-800 rounded-lg text-sm border border-blue-100">
                                📢 {msg.content}
                            </div>
                        )}

                        {msg.type === 'comment' && (
                            <div className="flex items-start gap-1">
                                <span className="text-blue-500 font-medium whitespace-nowrap">{msg.name}:</span>
                                <span className="text-gray-800">{msg.content}</span>
                            </div>
                        )}

                        {msg.type === 'enter' && (
                            <div className="text-orange-500/90 font-medium">
                                🎉 欢迎 <span className="text-orange-600 font-semibold">{msg.name}</span> {msg.content}
                            </div>
                        )}

                        {msg.type === 'like' && (
                            <div className="text-red-500 font-medium">
                                👍 <span className="text-red-600">{msg.name}</span> {msg.content}
                            </div>
                        )}
                    </div>
                ))}
            </div>

            {/* Input Area */}
            <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-100 p-3 pb-safe">
                <div className="flex items-center gap-2 max-w-md mx-auto">
                    <input
                        type="text"
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && handleSend()}
                        disabled={!config?.is_live}
                        placeholder="说点什么..."
                        className="flex-1 bg-gray-100 border-none rounded-full px-4 py-2 text-sm focus:ring-0"
                    />
                    <button
                        onClick={handleSend}
                        disabled={!inputValue.trim()}
                        className="p-2 text-gray-500 disabled:opacity-50 hover:text-blue-500 transition-colors flex items-center justify-center"
                    >
                        <FontAwesomeIcon icon={faPaperPlane} className="text-xl" />
                    </button>
                </div>
            </div>
        </div>
    );
}
